import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Portals children to the widget's top-level root container, escaping any
 * `overflow:auto` scroller they were declared inside.
 *
 * Why: a `position:fixed` element nested inside an `overflow` scroller is
 * mis-composited on iOS WebKit — GPU-promoted sibling content (e.g. product
 * card images) paints OVER the fixed overlay even though its z-index is higher.
 * Rendering the overlay as a child of the root container (a sibling of the
 * scrolling `<main>`, not a descendant) makes `position:fixed` and z-index
 * behave correctly across browsers. React context (i18n, fonts) is preserved
 * through the portal.
 *
 * The target is the first `.botilia-widget-root` in the same (shadow) tree —
 * the React mount point in the embed, or the modal root in the standalone SPA.
 * Falls back to `document.body` if neither is found.
 */
export const WidgetPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  // Ref callback resolves the target during commit (before paint → no flash).
  const setAnchor = useCallback((node: HTMLSpanElement | null) => {
    if (!node) return;
    const root = node.getRootNode();
    const target =
      root instanceof ShadowRoot || root instanceof Document
        ? (root.querySelector('.botilia-widget-root') as HTMLElement | null)
        : null;
    setContainer(target ?? document.body);
  }, []);

  return (
    <>
      <span ref={setAnchor} style={{ display: 'none' }} aria-hidden="true" />
      {container ? createPortal(children, container) : null}
    </>
  );
};
